import React, { useEffect, useMemo, useState } from "react";
import Datepicker from "tailwind-datepicker-react";
import { useSelector } from "react-redux";
import { clientHistory } from "../../utils/enum/enums";
import { sortByText } from "../../utils/sort/sort";
import { IsAllow } from "../../utils/allowHelper/AllowHelper";
import { useTranslator } from "../../context/translator";
import { useGetCompanyQuery, useGetUserByIdQuery } from "store/api/apiSlice";

const getLocalISOString = () => {
  const now = new Date();
  return new Date(
    now.getTime() - now.getTimezoneOffset() * 60000,
  ).toISOString();
};

const getFirstDayOfMonthISOString = () => {
  const now = new Date();
  const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
  return new Date(
    firstDay.getTime() - firstDay.getTimezoneOffset() * 60000,
  ).toISOString();
};

const ClientSearch = ({ setDateFrom, setDateTo, type, setType, setGetAll }) => {
  const { user } = useSelector((state) => state.user);
  const { data: userProfile, isLoading: userLoading } = useGetUserByIdQuery(
    user.id,
    {
      skip: !user?.id,
      pollingInterval: 60000,
      refetchOnFocus: true,
      refetchOnReconnect: true,
    },
  );

  const { data: company, isLoading: companyLoading } = useGetCompanyQuery(
    userProfile?.user?.companyId,
    {
      skip: !userProfile?.user?.companyId,
    },
  );
  const { translate } = useTranslator();
  const [showDateFrom, setShowDateFrom] = useState(false);
  const [showDateTo, setShowDateTo] = useState(false);
  const defaultDateFrom = new Date(getFirstDayOfMonthISOString());
  const defaultDateTo = new Date(getLocalISOString());
  const [companyAllow, setCompanyAllow] = useState([]);

  const options = useMemo(() => {
    const opts = [
      { id: clientHistory.ALL, text: translate("all") },
      { id: clientHistory.CALL, text: translate("call") },
      { id: clientHistory.MESSAGE, text: translate("message") },
      { id: clientHistory.EMAIL, text: "Email" },
      { id: clientHistory.DEPOSIT, text: translate("deposit") },
      { id: clientHistory.COMMENT, text: translate("comment") },
    ];
    return sortByText(opts);
  }, [translate]);

  const dateFromOptions = useMemo(
    () => ({
      title: translate("logs-date"),
      autoHide: true,
      todayBtn: true,
      clearBtn: false,
      minDate: new Date("2024-01-01"),
      theme: {
        background: "bg-gray-50 dark:bg-gray-700",
        text: "text-gray-900 dark:text-white",
        disabledText: "text-slate-300",
        input: "h-14",
      },
      icons: {
        prev: () => <span>{translate("previous")}</span>,
        next: () => <span>{translate("next")}</span>,
      },
      datepickerClassNames: "absolute",
      defaultDate: defaultDateFrom,
      language: "en",
      weekDays: ["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"],
      inputNameProp: "dateFrom",
      inputIdProp: "dateFrom",
      inputPlaceholderProp: translate("select-date"),
      inputDateFormatProp: {
        day: "numeric",
        month: "long",
        year: "numeric",
      },
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [translate],
  );

  const dateToOptions = useMemo(
    () => ({
      title: translate("logs-date"),
      autoHide: true,
      todayBtn: true,
      clearBtn: false,
      minDate: new Date("2024-01-01"),
      theme: {
        background: "bg-gray-50 dark:bg-gray-700",
        text: "text-gray-900 dark:text-white",
        disabledText: "text-slate-300",
        input: "h-14",
      },
      icons: {
        prev: () => <span>{translate("previous")}</span>,
        next: () => <span>{translate("next")}</span>,
      },
      datepickerClassNames: "absolute",
      defaultDate: defaultDateTo,
      language: "en",
      weekDays: ["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"],
      inputNameProp: "dateTo",
      inputIdProp: "dateTo",
      inputPlaceholderProp: translate("select-date"),
      inputDateFormatProp: {
        day: "numeric",
        month: "long",
        year: "numeric",
      },
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [translate],
  );

  const handleChangeDateFrom = (selectedDate) => {
    if (selectedDate instanceof Date) setDateFrom(selectedDate.toISOString());
  };
  const handleChangeDateTo = (selectedDate) => {
    if (selectedDate instanceof Date) setDateTo(selectedDate.toISOString());
  };

  const handleChangeType = (event) => {
    setType(event.target.value);
  };

  useEffect(() => {
    if (company?.allow) {
      setCompanyAllow({ ...company.allow });
    }
  }, [company]);

  if (userLoading || companyLoading) return null;

  return (
    <form className="grid gap-6 mb-6 md:grid-cols-4">
      <div>
        <label
          htmlFor="type"
          className="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
        >
          {translate("filter-by")}
        </label>
        <select
          id="type"
          className="p-4 bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg
                     focus:ring-blue-500 focus:border-blue-500 block w-full
                     dark:bg-gray-700 dark:border-gray-600 dark:text-white"
          value={type}
          onChange={handleChangeType}
        >
          {options.map((option) => {
            if (
              option.id === clientHistory.DEPOSIT &&
              !IsAllow(companyAllow, "SBFields")
            )
              return null;
            return (
              <option key={option.id} value={option.id}>
                {option.text}
              </option>
            );
          })}
        </select>
      </div>

      <div className="relative">
        <label
          htmlFor="date"
          className="block mb-1 text-sm font-medium text-gray-900 dark:text-white"
        >
          {translate("date-from")}
        </label>
        <Datepicker
          options={dateFromOptions}
          onChange={handleChangeDateFrom}
          show={showDateFrom}
          setShow={setShowDateFrom}
        />
      </div>
      <div className="relative">
        <label
          htmlFor="date"
          className="block mb-1 text-sm font-medium text-gray-900 dark:text-white"
        >
          {translate("date-to")}
        </label>
        <Datepicker
          options={dateToOptions}
          onChange={handleChangeDateTo}
          show={showDateTo}
          setShow={setShowDateTo}
        />
      </div>
      <div className="relative">
        <label
          htmlFor="date"
          className="block mb-1 text-sm font-medium text-gray-900 dark:text-white"
        >
          {translate("get-all-logs")}
        </label>
        <input
          type="checkbox"
          onChange={(e) => setGetAll(e.target.checked)}
          className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded
                     focus:ring-blue-500 dark:focus:ring-blue-600
                     dark:ring-offset-gray-800 focus:ring-2
                     dark:bg-gray-700 dark:border-gray-600"
        />
      </div>
    </form>
  );
};

export default ClientSearch;
